import { Injectable } from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';
import { MediaDepotAsset } from '@mira-avant/mtlibs';

export enum UploadState {
	PENDING = 'PENDING',
	IN_PROGRESS = 'IN_PROGRESS',
	DONE = 'DONE'
}

export interface UploadProgress {
	progress: number;
	state: UploadState;
}

export interface UploadResult {
	progress: UploadProgress;
	result?: MediaDepotAsset | MediaDepotAsset[];
}

@Injectable({
	providedIn: 'root',
})
export class MetabiUploaderService {
	private readonly BASE_PATH = 'upload';
	private readonly PROXY_DOMAIN = environment.proxyDomain;

	constructor(private api: ApiService) {}

	public uploadFile(
		file: File
	): Observable<UploadResult> {
		const formData = new FormData();
		formData.append('file', file, file.name);

		return this.api
			.upload<MediaDepotAsset | MediaDepotAsset[] | undefined>(this.BASE_PATH, formData, {
				reportProgress: true,
				observe: 'events',
			})
			.pipe(
				map((event: HttpEvent<MediaDepotAsset | MediaDepotAsset[] | undefined>) => this.handleEvent(event, file)),
				catchError(this.handleError.bind(this))
			);
	}

	private handleEvent(
		event: HttpEvent<MediaDepotAsset | MediaDepotAsset[] | undefined>,
		file: File
	): UploadResult {
		switch (event.type) {
			case HttpEventType.UploadProgress:
				const progress = Math.round((100 * event.loaded) / (event.total || file.size));
				return {
					progress: {
						progress,
						state: UploadState.IN_PROGRESS,
					},
				};
			case HttpEventType.Response:
				let result = event.body as MediaDepotAsset | MediaDepotAsset[] | undefined;

				if (Array.isArray(result)) {
					result = result.map(r => ({
						...r,
						url: r.url
					}));
				} else if (result) {
					result = {
						...result,
						url: result.url,
					};
				}

				return {
					progress: {
						progress: 100,
						state: UploadState.DONE,
					},
					result,
				};
			default:
				return {
					progress: {
						progress: 0,
						state: UploadState.PENDING,
					},
				};
		}
	}

	private handleError(error: any) {
		console.error('Upload error:', error);
		return throwError(() => new Error('An error occurred during the upload. Please try again.'));
	}
}
